import { LoadingIndicator } from '@qfa/react-components';
import { KINETIC_MODULES, StatusBars } from 'kinetic/modules';
import { Suspense, useContext, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useUserTenantPreference } from 'kineticUtils/hooks';
import { PREFERENCE_KEYS } from 'types/preferenceTypes';

import { StatusBarWrapperContext } from './context';
import { useRenderLazy } from 'kinetic/useRenderLazy';
import { MapContext } from 'components/maps/MapContext';
import { StatusBarPreference } from './types';
import StatusBar from 'components/StatusBar';

const STATUS_BAR_MODULES: KINETIC_MODULES[] = [
  'assetmanagement',
  'detectors',
  'events',
  'signals',
  'signs',
  'travel-time',
  'tolling',
  'parking',
  'roadassistance',
  'har',
  'video',
  'weather',
  'gates',
  'connectedvehicles',
  'road-conditions',
  'ramp-meter',
];
// TECH_DEBT
/**
 * This shared component knows about modules and has specific details about which modules have status bars
 *
 *  Redesign StatusBar feature to have module concerns defined in app, and properly support dyanmic imports
 *
 *  If a module without a status bar is first in the list. the status bar will never render:
 *   because the fallback dynamic import is () => null
 *  and the status bar menu is rendered by the Modules, instead of app
 */
const StatusBarWrapper: React.FunctionComponent = () => {
  const { activeStatusBar, isStatusBarOpen, setActiveStatusBar } = useContext(MapContext);
  const {
    value: preference,
    loading,
    setPreference,
  } = useUserTenantPreference<StatusBarPreference>({ key: PREFERENCE_KEYS.MAP_STATUS_BAR });

  const statusBarsElements = useRenderLazy({
    imports: StatusBars,
    pick: STATUS_BAR_MODULES,
  });

  useEffect(() => {
    if (activeStatusBar || loading) {
      return;
    }

    let defaultStatusBar = preference?.activeStatusBar;

    // check if the preference status bar is in the list of enabled module status bars, if not, fallback to the first one.
    // avoids edge case where status bar dissapears when you change licenses and have a disabled module in the preference
    if (!defaultStatusBar || !statusBarsElements.find((el) => el.key === defaultStatusBar)) {
      defaultStatusBar = statusBarsElements[0]?.key;
      if (defaultStatusBar) {
        // defaultStatusBar would only be falsy if there are no modules enabled that expose status bars
        setPreference({ ...preference, activeStatusBar: defaultStatusBar });
      }
    }
    if (defaultStatusBar) {
      setActiveStatusBar(defaultStatusBar);
    }
  }, [
    activeStatusBar,
    loading,
    preference,
    preference?.activeStatusBar,
    setPreference,
    setActiveStatusBar,
    statusBarsElements,
  ]);

  const activeBar = statusBarsElements.find(({ key }) => key === activeStatusBar) ?? null;

  return (
    <StatusBarWrapperContext.Provider
      value={{ statusBars: statusBarsElements.map((el) => el.key as string) }}
    >
      {!isStatusBarOpen ? null : (
        <ErrorBoundary fallbackRender={() => <StatusBar errorFallback />}>
          <Suspense fallback={<LoadingIndicator />}>{activeBar}</Suspense>
        </ErrorBoundary>
      )}
    </StatusBarWrapperContext.Provider>
  );
};

export default StatusBarWrapper;
